import { WIDTH_TO_HEIGHT_RATIO_THRESHOLD_TO_CROP_SUPER_WIDE, WIDTH_TO_HEIGHT_RATIO_THRESHOLD_TO_CROP_SUPER_TALL, WIDTH_TO_HEIGHT_RATIO_DEFAULT_LANDSCAPE, WIDTH_TO_HEIGHT_RATIO_DEFAULT_PORTRAIT, } from '../../constants';
import MediaService, { fallbackToLargestFile, } from '../../../utils/DeviationMediaService';
import cleanLinkSrc from '../../../utils/xss/cleanLinkSrc';
var ULTRA_WIDE_ASPECT = 4;
var ULTRA_TALL_ASPECT = 0.1;
export var getSourceImageRatio = function (deviation) {
    if (!deviation) {
        return 1;
    }
    var sourceFile = MediaService.getDeviationFile(deviation, 'fullview');
    if (!sourceFile) {
        // should never really happen but who knows
        // we're just being desperate here
        var thumbs = MediaService.getThumbSet(deviation);
        sourceFile = thumbs.pop();
    }
    if (!sourceFile) {
        // should really really never happen, but alas it does
        return 1;
    }
    var sourceImageRatio = (sourceFile.width || 0) / (sourceFile.height || 0);
    return sourceImageRatio;
};
export var isSourceImageVertical = function (deviation) {
    return getSourceImageRatio(deviation) < 1;
};
export var isSourceImageHorizontal = function (deviation) {
    return getSourceImageRatio(deviation) > 1;
};
export var getRestrictedImageRatio = function (sourceImageRatio) {
    if (sourceImageRatio > WIDTH_TO_HEIGHT_RATIO_THRESHOLD_TO_CROP_SUPER_WIDE) {
        return WIDTH_TO_HEIGHT_RATIO_DEFAULT_LANDSCAPE;
    }
    if (sourceImageRatio < WIDTH_TO_HEIGHT_RATIO_THRESHOLD_TO_CROP_SUPER_TALL) {
        return WIDTH_TO_HEIGHT_RATIO_DEFAULT_PORTRAIT;
    }
    return sourceImageRatio;
};
/** @deprecated */
export function getSrcSet(deviation) {
    if (!deviation) {
        return '';
    }
    var thumbs = MediaService.getThumbSet(deviation);
    var pre = MediaService.getDeviationFile(deviation, 'preview');
    var full = MediaService.getDeviationFile(deviation, 'fullview');
    if (full) {
        thumbs.push(full);
    }
    if (pre) {
        thumbs.push(pre);
    }
    var sizes = {};
    var files = [];
    var _loop_1 = function (file) {
        // Dedupe
        if (files.find(function (f) { return f === file.src; })) {
            return "continue";
        }
        files.push(file.src);
        // Bucket sizes
        var size = Math.floor((file.width || 0) / 100) * 100;
        if (size <= 0) {
            return "continue";
        }
        var sizeKey = "".concat(size, "w");
        sizes[sizeKey] = "".concat(cleanLinkSrc(file.src), " ").concat(sizeKey);
    };
    for (var _i = 0, thumbs_1 = thumbs; _i < thumbs_1.length; _i++) {
        var file = thumbs_1[_i];
        _loop_1(file);
    }
    var srcset = [];
    for (var size in sizes) {
        srcset.push("".concat(sizes[size]));
    }
    return srcset.join(', ');
}
export function getInfoToDisplayImageDeviation(_a) {
    var deviation = _a.deviation, containerHeight = _a.height, containerWidth = _a.width, allowPreview = _a.allowPreviewUsage, greenLikeCrop = _a.greenLikeCrop, noCropping = _a.noCropping;
    var sourceImageRatio = getSourceImageRatio(deviation);
    // derive width based on sourceImageRation, if only height was passed
    if (!containerHeight) {
        containerWidth = containerWidth || 0; // TS
        containerHeight =
            containerWidth / getRestrictedImageRatio(sourceImageRatio);
    }
    // derive height based on sourceImageRation, if only width was passed
    if (!containerWidth) {
        containerHeight = containerHeight || 0; // TS
        containerWidth =
            containerHeight * getRestrictedImageRatio(sourceImageRatio);
    }
    var pickedImage = MediaService.getSmallestThumbThatFullyCovers(deviation, containerWidth, containerHeight, fallbackToLargestFile, { allowPreview: allowPreview, allowSquareCrop: greenLikeCrop });
    var imgStyles = {};
    if (pickedImage) {
        if (noCropping) {
            imgStyles = {
                objectFit: 'scale-down',
                objectPosition: '50%',
            };
        }
        else {
            imgStyles = getImageStylesForImageCropping(pickedImage, containerWidth, containerHeight, greenLikeCrop);
        }
        // Handle super super tall
        // Handle super super wide
        if (!noCropping &&
            (sourceImageRatio > ULTRA_WIDE_ASPECT ||
                sourceImageRatio < ULTRA_TALL_ASPECT)) {
            imgStyles.objectFit = 'none';
        }
        return {
            height: containerHeight,
            width: containerWidth,
            imgStyles: imgStyles,
            pickedImage: pickedImage,
        };
    }
}
/**
 * Image cropping settings:
 */
var portraitHeightPercentage = 1.08; // 108%
var portraitBackgroundPercentage = 0.08; // 8%
var smallImagePercentage = 0.25; // 25%
var smallImageOtherPercentage = 0.3; // 30%
/**
 * This applies the thumb cropping rules here: https://zpl.io/VxRRwdW
 *
 * Tried to keep the rules as clear as possible so its verbose on purpose
 *
 * * Smaller Images
 *  * Calculate the difference between image length and container length
 *  * When diff is < 25% (of the container length) for both sides we scale up (cover)
 *  * When diff for one is < 25% and the other is < 30% we scale up the nearest keeping aspect (contain)
 *  * When nothing falls into above we dont scale at all (scale-down)
 * Landscapes & squares
 *  * Just use cover and center (cover)
 * Portraits
 *  * We scale the height in proportion to the width and then:
 *  * If the scaled height is > 108% we use the negative -8% margin (cover)
 *  * If the scaled height is between 100 and 108 we use bottom alignment (cover)
 * Default
 *  * Use cover and center
 *
 * @param pickedImage
 * @param containerWidth
 * @param containerHeight
 */
function getImageStylesForImageCropping(pickedImage, containerWidth, containerHeight, greenLikeCrop) {
    if (greenLikeCrop === void 0) { greenLikeCrop = false; }
    var pickedImageWidth = pickedImage.width || 0;
    var pickedImageHeight = pickedImage.height || 0;
    var pickedImageWidthHeightRatio = containerWidth / pickedImageWidth;
    if (greenLikeCrop) {
        if (pickedImageWidth > containerWidth &&
            pickedImageHeight > containerHeight) {
            return {
                objectFit: 'cover',
                objectPosition: '25% 25%',
            };
        }
        return {
            objectFit: 'cover',
            objectPosition: '50% 50%',
        };
    }
    // Small image handling - anything smaller than the container
    // Note: the default case is that both are bigger than 75% and we just use cover to handle it for us
    if (pickedImageWidth < containerWidth ||
        pickedImageHeight < containerHeight) {
        var pickedImageWidthDiff = containerWidth - pickedImageWidth;
        var pickedImageHeightDiff = containerHeight - pickedImageHeight;
        var pickedImageWidthDiffPc = pickedImageWidthDiff / containerWidth;
        var pickedImageHeightDiffPc = pickedImageHeightDiff / containerHeight;
        // Case: both are smaller than 75% (25% diff)
        // Scale both using cover size
        if (pickedImageWidthDiffPc < smallImagePercentage &&
            pickedImageHeightDiffPc < smallImagePercentage) {
            return {
                objectFit: 'cover',
                objectPosition: getSmallBgPosition(pickedImage, containerWidth, containerHeight, 'center'),
            };
        }
        // Case: one is smaller than 75% and other is 70% (25% diff and 30% diff)
        // Scale up using a single dimension
        if (pickedImageWidthDiffPc < smallImagePercentage &&
            pickedImageHeightDiffPc < smallImageOtherPercentage) {
            return {
                objectFit: 'contain',
            };
        }
        if (pickedImageHeightDiffPc < smallImagePercentage &&
            pickedImageWidthDiffPc < smallImageOtherPercentage) {
            return {
                objectFit: 'contain',
            };
        }
        // Case: Image is very small display it without scaling
        return {
            objectFit: 'scale-down',
        };
    }
    // Handle image big enough for the container
    // Squares and Landscapes
    // Scale using cover size
    if (pickedImageWidth === pickedImageHeight ||
        pickedImageWidth > pickedImageHeight) {
        return {
            objectFit: 'cover',
            objectPosition: '50% 50%',
        };
    }
    // Portraits
    if (pickedImageWidth < pickedImageHeight) {
        var scaledHeight = pickedImageHeight * pickedImageWidthHeightRatio;
        var containerThreshold = portraitHeightPercentage * containerHeight;
        // Case: Height > 108%
        // offsets the background by -8% and we manually set the BG size
        // anything else seemed to hard crop the background so the negative offset
        // would show blank area.
        if (scaledHeight >= containerThreshold) {
            var offset = portraitBackgroundPercentage * scaledHeight;
            return {
                objectFit: 'cover',
                objectPosition: "50% -".concat(offset, "px"),
            };
        }
        // Case: Height < 108% and > 100%
        // Crops from the bottom
        if (scaledHeight >= containerHeight && scaledHeight < containerThreshold) {
            return {
                objectFit: 'cover',
                objectPosition: "50% 100%",
            };
        }
    }
    // Default should not really happen but we fallback
    // to cover size because browser will do its best then...
    return {
        objectFit: 'cover',
    };
}
function getSmallBgPosition(pickedImage, containerWidth, containerHeight, fallback) {
    var pickedImageWidth = pickedImage.width || 0;
    var pickedImageHeight = pickedImage.height || 0;
    var pickedImageWidthHeightRatio = containerWidth / pickedImageWidth;
    // Portraits
    if (pickedImageWidth < pickedImageHeight) {
        var scaledHeight = pickedImageHeight * pickedImageWidthHeightRatio;
        var containerThreshold = portraitHeightPercentage * containerHeight;
        // Case: Height > 108%
        // offsets the background by -8% and we manually set the BG size
        if (scaledHeight >= containerThreshold) {
            var offset = portraitBackgroundPercentage * scaledHeight;
            return "50% -".concat(offset, "px");
        }
        // Case: Height < 108% and > 100%
        // Crops from the bottom
        if (scaledHeight >= containerHeight && scaledHeight < containerThreshold) {
            return "50% 100%";
        }
    }
    return fallback;
}
//# sourceMappingURL=_helpers.js.map